import type { ViewJob } from "../../../__generated__/types.gqlgenerated";
import type { Locale } from "../../../shared/I18n";
import type { ViewjobQueryResult } from "../__generated__/viewjob.gqlgenerated";

import {
  useConditionalHiringEventsData,
  type HiringEventData,
} from "./useConditionalHiringEventsData";
import { useViewJobData } from "./useViewJobData";
import { useViewjobProctorAllocation } from "../../proctor/__generated__/ViewjobProctorContext";
import { HeRnViewjobOverviewTst } from "../../proctor/__generated__/ViewjobProctorSpecifications";
import { isHiringEvent } from "../components/core/HiringEvents/utils/isHiringEvent";
import getJobKeyFromUrl from "../util/getJobKeyFromUrl";
import { normalizeViewJobUrl } from "../util/normalizeViewJobUrl";

import { useMemo, useRef } from "react";

export type ViewJobInitialDataResult = {
  loading: boolean;
  data: {
    viewJobData: ViewjobQueryResult["data"];
    hiringEventsData?: HiringEventData | undefined;
  };
  error: Error | undefined;
  queryStatus: "idle" | "active" | "success" | "error";
  refetch: () => void;
  fetchStartTimeMs: number | null;
  fetchEndTimeMs: number | null;
};

export const useViewJobInitialData = (
  url: string | null,
  locale: Locale,
): ViewJobInitialDataResult => {
  const jobKey = getJobKeyFromUrl(url);
  const normalizedUrl = normalizeViewJobUrl(url, locale);
  const fetchStartTimeMsRef = useRef<number | null>(null);
  const fetchEndTimeMsRef = useRef<number | null>(null);

  const heRnViewJobOverviewTestAllocation = useViewjobProctorAllocation(HeRnViewjobOverviewTst);

  const { data, loading, error, refetch } = useViewJobData(normalizedUrl || "", jobKey || "");
  const criticalError =
    error && !(data?.viewjob?.job && data?.viewjob?.details) ? error : undefined;

  // Track fetch start timestamp (only when query is actually loading)
  if (loading && fetchStartTimeMsRef.current === null) {
    fetchStartTimeMsRef.current = Date.now();
  }

  // Track fetch end timestamp (only after we've seen a loading state)
  if (
    !loading &&
    data &&
    fetchStartTimeMsRef.current !== null &&
    fetchEndTimeMsRef.current === null
  ) {
    fetchEndTimeMsRef.current = Date.now();
  }
  // Determine if this is a hiring event from the ViewJob model
  const shouldFetchHiringEventData =
    heRnViewJobOverviewTestAllocation?.isTest &&
    !!data?.viewjob &&
    isHiringEvent(data.viewjob as ViewJob);

  // Conditionally fetch hiring events data based on ViewJob response
  const hiringEventsData = useConditionalHiringEventsData(shouldFetchHiringEventData, jobKey);

  const queryStatus = useMemo(() => {
    // If ViewJob is still loading or failed, use its status
    if (loading) return "active";
    if (criticalError) return "error";
    if (!data) return "idle";

    // ViewJob succeeded - now check hiring events if needed
    if (shouldFetchHiringEventData) {
      if (!hiringEventsData.called || hiringEventsData.loading) return "active";
      if (hiringEventsData.error) return "error";
    }

    // All required data is ready
    return "success";
  }, [loading, criticalError, data, shouldFetchHiringEventData, hiringEventsData]);

  const retryRequest = () => {
    // Reset timestamps for new fetch
    fetchStartTimeMsRef.current = null;
    fetchEndTimeMsRef.current = null;
    void refetch();

    // Also refetch hiring events data if it was previously fetched
    if (shouldFetchHiringEventData && hiringEventsData.refetch && hiringEventsData.called) {
      void hiringEventsData.refetch();
    }
  };

  return {
    loading: queryStatus === "active",
    data: {
      viewJobData: data,
      hiringEventsData: hiringEventsData.hiringEvent,
    },
    error: criticalError || hiringEventsData.error,
    queryStatus,
    refetch: retryRequest,
    fetchStartTimeMs: fetchStartTimeMsRef.current,
    fetchEndTimeMs: fetchEndTimeMsRef.current,
  };
};
